import { Network } from '@capacitor/network';
import { Auth } from 'aws-amplify';

import {
  clearCachedSessionFromSecureStorage,
  readCachedSessionFromSecureStorage,
  writeCachedSessionToSecureStorage,
} from './secure-session-storage';

export interface CachedSession {
  token: string;
  accessToken: string;
  refreshToken: string;
  expiration: number; // Unix timestamp in seconds
}

let cachedSessionInMemory: CachedSession | null = null;

function isSessionValid(session: CachedSession): boolean {
  return session.expiration > Math.floor(Date.now() / 1000);
}

/* istanbul ignore next -- @preserve */
export async function getCachedSession(): Promise<CachedSession | null> {
  if (cachedSessionInMemory) {
    if (isSessionValid(cachedSessionInMemory)) {
      return cachedSessionInMemory;
    }

    cachedSessionInMemory = null;
    await clearCachedSessionFromSecureStorage();
  }

  const secureCachedSession = await readCachedSessionFromSecureStorage();
  if (!secureCachedSession) {
    return null;
  }

  if (!isSessionValid(secureCachedSession)) {
    await clearCachedSessionFromSecureStorage();
    return null;
  }

  cachedSessionInMemory = secureCachedSession;
  return secureCachedSession;
}

/**
 * Fetches the current session from Amplify Auth and updates the local cache.
 * In a monorepo setup, Amplify does not work properly when placed in a shared folder.
 * To avoid issues, Amplify Auth must be passed explicitly as a parameter.
 */
/* istanbul ignore next -- @preserve */
export async function getCurrentAuthSession(AmplifyAuth: typeof Auth): Promise<CachedSession> {
  let session: CachedSession | null = null;

  const { connected: isOnline } = await Network.getStatus();

  if (isOnline) {
    try {
      // Fetch fresh session from Amplify when online
      const awsSession = await AmplifyAuth.currentSession();
      const idToken = awsSession.getIdToken();
      const accessToken = awsSession.getAccessToken();
      const refreshToken = awsSession.getRefreshToken();

      const newSession: CachedSession = {
        token: idToken.getJwtToken(),
        accessToken: accessToken.getJwtToken(),
        refreshToken: refreshToken.getToken(),
        expiration: idToken.getExpiration(),
      };

      cachedSessionInMemory = newSession;
      await writeCachedSessionToSecureStorage(newSession);
      session = newSession;
    } catch (error) {
      console.error('Error fetching current session:', error);
      // Fall back to the cached session if available
      session = await getCachedSession();
    }
  } else {
    // Offline: use cached session if available
    session = await getCachedSession();
  }

  if (!session) {
    throw new Error('No session available');
  }

  return session;
}

/**
 * Logs the user out via Amplify Auth and updates the local cache.
 * In a monorepo setup, Amplify does not work properly when placed in a shared folder.
 * To avoid issues, Amplify Auth must be passed explicitly as a parameter.
 */
export async function signOutOfAuthSession(AmplifyAuth: typeof Auth): Promise<void> {
  cachedSessionInMemory = null;
  await clearCachedSessionFromSecureStorage();
  await AmplifyAuth.signOut();
}
